import React, { useState } from "react";
import axios from "axios";
import { X } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const EMOJIS = [
  "📁","🚀","💼","🏠","🎯","📊","✨","🔧","🌱","🎨",
  "📱","🏋️","🌍","💡","🎵","📸","🐶","⚽","🌴","💰",
  "🔬","📚","🎭","🏆","🍕","✈️","🎓","💊","🏗️","🌊",
];

const labelStyle = {
  fontFamily: "'Manrope', sans-serif",
  color: "#1A4D2E",
  fontSize: "0.8rem",
  fontWeight: 600,
  display: "block",
  marginBottom: "0.35rem",
};

const inputStyle = {
  width: "100%",
  padding: "0.55rem 0.75rem",
  borderRadius: "0.5rem",
  border: "1.5px solid rgba(26,77,46,0.15)",
  background: "rgba(255,255,255,0.65)",
  color: "#0F172A",
  fontSize: "0.875rem",
  fontFamily: "'Manrope', sans-serif",
  outline: "none",
  boxSizing: "border-box",
};

export default function CreateProjectModal({ onClose, onCreated, token, activeCount }) {
  const [title, setTitle] = useState("");
  const [emoji, setEmoji] = useState("📁");
  const [category, setCategory] = useState("work");
  const [targetDate, setTargetDate] = useState("");
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const atLimit = activeCount >= 6;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title.trim() || atLimit) return;
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.post(
        `${BACKEND_URL}/api/projects`,
        { title: title.trim(), emoji, category, target_completion: targetDate || null, notes: notes || null },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      onCreated(data);
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to create project");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      data-testid="create-project-modal"
      style={{
        position: "fixed", inset: 0, zIndex: 100,
        background: "rgba(0,0,0,0.20)",
        backdropFilter: "blur(4px)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: "1rem",
      }}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div style={{
        background: "rgba(255,255,255,0.93)",
        backdropFilter: "blur(24px)",
        WebkitBackdropFilter: "blur(24px)",
        border: "1px solid rgba(255,255,255,0.65)",
        borderRadius: "1.25rem",
        boxShadow: "0 20px 60px rgba(26,77,46,0.14)",
        width: "100%", maxWidth: "460px",
        padding: "1.75rem",
      }}>
        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1.25rem" }}>
          <h2 style={{ fontFamily: "'Manrope', sans-serif", fontSize: "1.1rem", fontWeight: 700, color: "#1A4D2E", margin: 0 }}>
            New Project
          </h2>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: "#6b7280", display: "flex", padding: 0 }}>
            <X size={18} />
          </button>
        </div>

        {atLimit && (
          <div style={{ background: "rgba(251,191,36,0.12)", border: "1px solid rgba(251,191,36,0.4)", borderRadius: "0.5rem", padding: "0.625rem 0.875rem", marginBottom: "1rem", fontFamily: "'Manrope', sans-serif", color: "#92400e", fontSize: "0.8rem" }}>
            You have 6 active projects (the maximum). Archive one to create a new project.
          </div>
        )}

        <form onSubmit={handleSubmit}>
          {/* Emoji + Title */}
          <div style={{ display: "flex", gap: "0.75rem", marginBottom: "1rem", alignItems: "flex-end" }}>
            <div>
              <label style={labelStyle}>Icon</label>
              <div style={{ fontSize: "1.75rem", width: "46px", height: "40px", display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(26,77,46,0.06)", borderRadius: "0.5rem", border: "1px solid rgba(26,77,46,0.12)" }}>
                {emoji}
              </div>
            </div>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>Project Title *</label>
              <input
                data-testid="project-title-input"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                required
                placeholder="e.g. Market Guard Pro"
                style={inputStyle}
              />
            </div>
          </div>

          {/* Emoji picker */}
          <div style={{ marginBottom: "1rem" }}>
            <label style={labelStyle}>Choose Icon</label>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "0.3rem" }}>
              {EMOJIS.map((e) => (
                <button
                  key={e} type="button"
                  data-testid={`emoji-option-${e}`}
                  onClick={() => setEmoji(e)}
                  style={{
                    width: "32px", height: "32px", fontSize: "1rem",
                    background: emoji === e ? "rgba(26,77,46,0.12)" : "transparent",
                    border: emoji === e ? "1.5px solid rgba(26,77,46,0.35)" : "1px solid rgba(26,77,46,0.08)",
                    borderRadius: "0.375rem", cursor: "pointer",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    transition: "background 0.15s",
                  }}
                >{e}</button>
              ))}
            </div>
          </div>

          {/* Category */}
          <div style={{ marginBottom: "1rem" }}>
            <label style={labelStyle}>Category</label>
            <div style={{ display: "flex", gap: "0.5rem" }}>
              {["work", "personal"].map((cat) => (
                <button
                  key={cat} type="button"
                  data-testid={`category-${cat}`}
                  onClick={() => setCategory(cat)}
                  style={{
                    padding: "0.38rem 1.1rem", borderRadius: "2rem",
                    border: category === cat
                      ? (cat === "work" ? "1.5px solid #3d6b1f" : "1.5px solid #d4a574")
                      : "1px solid rgba(26,77,46,0.15)",
                    background: category === cat
                      ? (cat === "work" ? "rgba(61,107,31,0.12)" : "rgba(212,165,116,0.15)")
                      : "transparent",
                    color: category === cat ? (cat === "work" ? "#3d6b1f" : "#92400e") : "#6b7280",
                    fontSize: "0.78rem", fontWeight: 700,
                    fontFamily: "'Manrope', sans-serif",
                    cursor: "pointer", textTransform: "uppercase", letterSpacing: "0.04em",
                  }}
                >{cat}</button>
              ))}
            </div>
          </div>

          {/* Target date */}
          <div style={{ marginBottom: "1rem" }}>
            <label style={labelStyle}>Target Completion (optional)</label>
            <input type="date" value={targetDate} onChange={(e) => setTargetDate(e.target.value)} style={inputStyle} />
          </div>

          {/* Notes */}
          <div style={{ marginBottom: "1.25rem" }}>
            <label style={labelStyle}>Notes (optional)</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Any context or goals..."
              rows={2}
              style={{ ...inputStyle, resize: "vertical", minHeight: "58px" }}
            />
          </div>

          {error && (
            <p style={{ color: "#dc2626", fontSize: "0.8rem", fontFamily: "'Manrope', sans-serif", marginBottom: "0.75rem", margin: "0 0 0.75rem" }}>{error}</p>
          )}

          <div style={{ display: "flex", gap: "0.625rem", justifyContent: "flex-end" }}>
            <button
              type="button" onClick={onClose}
              style={{ padding: "0.5rem 1.25rem", borderRadius: "2rem", border: "1px solid rgba(26,77,46,0.2)", background: "transparent", color: "#6b7280", fontFamily: "'Manrope', sans-serif", fontSize: "0.875rem", fontWeight: 600, cursor: "pointer" }}
            >Cancel</button>
            <button
              type="submit"
              data-testid="create-project-submit-btn"
              disabled={loading || atLimit || !title.trim()}
              style={{
                padding: "0.5rem 1.5rem", borderRadius: "2rem", border: "none",
                background: loading || atLimit || !title.trim() ? "rgba(61,107,31,0.4)" : "#3d6b1f",
                color: "#fff", fontFamily: "'Manrope', sans-serif", fontSize: "0.875rem", fontWeight: 600,
                cursor: loading || atLimit ? "not-allowed" : "pointer",
              }}
            >{loading ? "Creating..." : "Create Project"}</button>
          </div>
        </form>
      </div>
    </div>
  );
}
